import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { HttpIoService } from '../communication/http-io.service';
import { MainStateService } from '../state/app-main-state.service';

/**
 * =====================================================================
 *  HOSTED HOLD — pre-autorizzazione pagata sulla PAGINA DEL PSP.
 * =====================================================================
 *
 * "hosted", mai "wallet": nel repo `wallet` è già il credito prepagato
 * (energy wallet). Che l'utente scelga Apple Pay, Google Pay o una carta
 * sulla pagina è un dettaglio, non l'identità del meccanismo.
 *
 * Flusso: POST hosted-hold → apertura del link nel browser di sistema
 * (SFSafariViewController / Custom Tabs: l'utente non esce dall'app) →
 * alla chiusura si chiede al backend lo stato reale. L'esito NON si deduce
 * mai dall'URL di rientro (manomettibile): lo dice il PSP.
 *
 * L'app conserva l'hold pagato e non ancora consumato da un avvio: senza,
 * un secondo tentativo creerebbe un secondo hold e **bloccherebbe i fondi
 * dell'utente due volte**. Se l'OS ha ucciso l'app mentre era nel browser,
 * lo stesso hold rientra dal deep link `?hosted_hold={holdRef}` (vedi
 * `handleReturn`) — stessa rete di sicurezza del `successActionUid` della
 * registrazione carta.
 */

export type HostedHoldStatus =
  | 'AUTHORIZED'   // hold buono: la ricarica può partire
  | 'PENDING'      // non pagato / non completato
  | 'FAILED'       // rifiutato o annullato dal PSP
  | 'UNKNOWN'      // riferimento non riconosciuto o scaduto
  | 'ERROR'        // non siamo riusciti a chiedere (rete, backend): diverso da "rifiutato"
  | 'REDIRECTED';  // solo web: si esce dall'app, l'esito arriva al rientro

export interface IHostedHoldCreated {
  paymentId: string;
  holdRef: string;
  href: string;
  amount: number;
  currency: string;
}

export interface IHostedHoldVerification {
  status: HostedHoldStatus;
  paymentId: string | null;
  authorizedAmount?: number;
  currency?: string;
  paymentMethod?: 'APPLE_PAY' | 'GOOGLE_PAY' | 'CARD_HOSTED';
  reason?: string;
}

export type HostedHoldPhase = 'idle' | 'creating' | 'paying' | 'verifying';

const CTRL = 'payment-core/hosted-hold';
const I18N = 'APP.INSTALLATION.HOSTED_PAY';

@Injectable({ providedIn: 'root' })
export class HostedHoldService {
  /** Hold pagato e non ancora usato per avviare una sessione. */
  private pendingPaymentId: string | null = null;
  /** Browser di sistema aperto: il rientro dal deep link lo chiude e lascia concludere il flusso. */
  private browserOpen = false;
  /** Su web `?hosted_hold=` resta nell'URL e riemette a ogni navigazione: un esito, un messaggio. */
  private handledRef: string | null = null;

  constructor(
    private readonly HIO: HttpIoService,
    private readonly mainStateService: MainStateService,
  ) { }

  /** Superficie hosted visibile? Decisione già presa dal backend (stadio rollout + isAdmin). */
  isAvailable(): boolean {
    return !!this.mainStateService.getUser()?.hostedPayments;
  }

  /**
   * Autorizza un hold da spendere all'avvio. Restituisce SEMPRE un esito
   * leggibile: chi chiama avvia la sessione solo su `AUTHORIZED`.
   */
  async authorize(onPhase: (phase: HostedHoldPhase) => void = () => { }): Promise<IHostedHoldVerification> {
    // Hold già pagato (avvio fallito dopo il pagamento, o rientro dal deep link):
    // si riusa. Crearne un altro bloccherebbe i fondi una seconda volta.
    if (this.pendingPaymentId) {
      onPhase('verifying');
      const known = await this.verify(this.pendingPaymentId);
      if (known.status === 'AUTHORIZED') return known;
      this.pendingPaymentId = null; // non più spendibile: si riparte da zero
    }

    onPhase('creating');
    let created: IHostedHoldCreated;
    try {
      created = await this.HIO.post<IHostedHoldCreated, {}>(CTRL, {});
    } catch (err) {
      return { status: 'ERROR', paymentId: null, reason: this.reasonOf(err) };
    }
    if (!created?.href || !created?.paymentId) {
      return { status: 'ERROR', paymentId: null, reason: 'link di pagamento non disponibile' };
    }

    onPhase('paying');
    if (!this.mainStateService.getNative()) {
      // Web/PWA: nessun browser di sistema da chiudere. Si esce dall'app e si
      // rientra dal redirect `?hosted_hold=` → stessa strada del recovery.
      window.location.href = created.href;
      return { status: 'REDIRECTED', paymentId: created.paymentId };
    }
    await this.openAndWaitClose(created.href);

    onPhase('verifying');
    const outcome = await this.verify(created.paymentId);
    if (outcome.status === 'AUTHORIZED') this.pendingPaymentId = created.paymentId;
    return outcome;
  }

  /**
   * Rientro dalla pagina hosted (`?hosted_hold={holdRef}`): il paymentId in
   * memoria può essere perso (app killata dall'OS), quindi si risolve dal
   * riferimento che ha viaggiato nell'URL.
   *
   * `null` = c'è un flusso vivo che se ne sta già occupando (Android: l'app
   * link riapre l'app col Custom Tab ancora davanti): chiuderlo fa concludere
   * quel flusso, che verifica per conto suo. Un doppio messaggio all'utente
   * per lo stesso pagamento è peggio di nessun messaggio.
   */
  async handleReturn(holdRef: string): Promise<IHostedHoldVerification | null> {
    if (this.browserOpen) {
      await Browser.close().catch(() => { /* già chiuso: il flusso prosegue lo stesso */ });
      return null;
    }
    if (!holdRef || holdRef === this.handledRef) return null;
    const outcome = await this.verifyByRef(holdRef);
    // Marcato come gestito solo se il PSP ha risposto davvero: su errore di rete
    // il riferimento resta ritentabile, altrimenti un hold pagato resterebbe
    // sconosciuto all'app e il prossimo avvio ne creerebbe un secondo.
    if (outcome.status !== 'ERROR') this.handledRef = holdRef;
    if (outcome.status === 'AUTHORIZED') this.pendingPaymentId = outcome.paymentId;
    return outcome;
  }

  /** Hold speso da un avvio andato a buon fine: non va riproposto. */
  consume(): void {
    this.pendingPaymentId = null;
  }

  /** Chiave i18n del messaggio d'errore: una sola mappa per tutte le superfici. */
  messageKey(status: HostedHoldStatus): string {
    switch (status) {
      case 'AUTHORIZED': return `${I18N}.AUTHORIZED`;
      case 'FAILED': return `${I18N}.DECLINED`;
      case 'PENDING': return `${I18N}.NOT_COMPLETED`;
      case 'UNKNOWN': return `${I18N}.UNKNOWN`;
      default: return `${I18N}.ERROR`;
    }
  }

  /** Stato verificato dal backend contro il PSP; errore di rete ⇒ `ERROR`, mai `AUTHORIZED`. */
  private async verify(paymentId: string): Promise<IHostedHoldVerification> {
    try {
      const rv = await this.HIO.get<IHostedHoldVerification>(`${CTRL}/${encodeURIComponent(paymentId)}`);
      // Risposta vuota: mai restituire undefined a chi deve leggere `.status`.
      // Un esito indeterminabile vale ERROR, non "autorizzato".
      return rv ?? { status: 'ERROR', paymentId, reason: 'risposta vuota dal backend' };
    } catch (err) {
      return { status: 'ERROR', paymentId, reason: this.reasonOf(err) };
    }
  }

  private async verifyByRef(holdRef: string): Promise<IHostedHoldVerification> {
    try {
      const rv = await this.HIO.get<IHostedHoldVerification>(`${CTRL}/by-ref/${encodeURIComponent(holdRef)}`);
      return rv ?? { status: 'ERROR', paymentId: null, reason: 'risposta vuota dal backend' };
    } catch (err) {
      return { status: 'ERROR', paymentId: null, reason: this.reasonOf(err) };
    }
  }

  /**
   * Apre il browser di sistema e aspetta che venga chiuso.
   * ponytail: unico segnale d'uscita `browserFinished` — il rientro da deep
   * link chiama `Browser.close()`, che lo fa scattare comunque. Se un giorno
   * una piattaforma non lo emettesse, servirebbe un secondo segnale (resume).
   */
  private async openAndWaitClose(url: string): Promise<void> {
    this.browserOpen = true;
    let onClosed: () => void = () => { };
    const closed = new Promise<void>((resolve) => { onClosed = resolve; });
    // registrato PRIMA di open: su Custom Tabs la chiusura può arrivare subito
    const handle = await Browser.addListener('browserFinished', () => onClosed());
    try {
      await Browser.open({ url, presentationStyle: 'popover' });
      await closed;
    } finally {
      await handle.remove().catch(() => { });
      this.browserOpen = false;
    }
  }

  private reasonOf(err: unknown): string {
    return err instanceof Error ? err.message : String(err ?? '');
  }
}
